import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Search,
  Plus,
  Pencil,
  Trash2,
  Star,
  CheckCircle2,
  Image as ImageIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  useLazyGetEmailBrandingProfilesQuery,
  useDeleteEmailBrandingProfileMutation,
  useSetDefaultEmailBrandingProfileMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import { IEmailBrandingProfile } from "../../../types/Request/AdminRequest";

const BrandingProfilesScreen: React.FC = () => {
  const navigate = useNavigate();

  const [getBrandingProfiles, { data, isLoading, isFetching, isError }] =
    useLazyGetEmailBrandingProfilesQuery();

  const [deleteBrandingProfile, { isLoading: isDeleting }] =
    useDeleteEmailBrandingProfileMutation();

  const [setDefaultBrandingProfile, { isLoading: isSettingDefault }] =
    useSetDefaultEmailBrandingProfileMutation();

  const [profiles, setProfiles] = useState<IEmailBrandingProfile[]>([]);
  const [search, setSearch] = useState("");

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultId, setDefaultId] = useState<number | null>(null);
  const [showDefaultDialog, setShowDefaultDialog] = useState(false);
  const [imageError, setImageError] = useState(false);
  useEffect(() => {
    getBrandingProfiles();
  }, [getBrandingProfiles]);

  useEffect(() => {
    if (data) {
      setProfiles(data);
    }
  }, [data]);

  const filteredProfiles = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) {
      return profiles;
    }

    return profiles.filter((profile) => {
      return (
        (profile.name ?? "").toLowerCase().includes(value) ||
        (profile.companyName ?? "").toLowerCase().includes(value) ||
        (profile.tagLine ?? "").toLowerCase().includes(value) ||
        (profile.supportEmail ?? "").toLowerCase().includes(value) ||
        (profile.websiteUrl ?? "").toLowerCase().includes(value)
      );
    });
  }, [profiles, search]);

  const handleDelete = async () => {
    if (!deleteId) {
      return;
    }

    try {
      await deleteBrandingProfile(deleteId).unwrap();

      setShowDeleteDialog(false);
      setDeleteId(null);

      await getBrandingProfiles();
    } catch (error) {
      console.error("Delete branding profile failed:", error);
    }
  };

  const handleSetDefault = async () => {
    if (!defaultId) {
      return;
    }

    try {
      await setDefaultBrandingProfile(defaultId).unwrap();

      setShowDefaultDialog(false);
      setDefaultId(null);

      await getBrandingProfiles();
    } catch (error) {
      console.error("Set default branding profile failed:", error);
    }
  };

  const handleCreate = () => {
    navigate("/admin/adminemailtemplates/branding/create");
  };

  const handleEdit = (id: number) => {
    navigate(`/admin/adminemailtemplates/branding/edit/${id}`);
  };

  const BrandingLogo: React.FC<{
    src?: string | null;
    name: string;
  }> = ({ src, name }) => {
    const [hasError, setHasError] = useState(false);

    if (!src || hasError) {
      return (
        <div className="flex h-full w-full items-center justify-center bg-slate-50">
          <ImageIcon size={18} className="text-slate-400" />
        </div>
      );
    }

    return (
      <img
        src={src}
        alt={name}
        className="h-full w-full object-contain p-1.5"
        onError={() => setHasError(true)}
      />
    );
  };

  if (isLoading || isFetching) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex min-h-[420px] flex-col items-center justify-center gap-3">
          <Loader2 size={32} className="animate-spin text-primary" />

          <p className="text-sm text-slate-500">Loading branding profiles...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {/* ===================================================== */}
        {/* Header */}
        {/* ===================================================== */}

        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Branding Profiles
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Manage branding profiles used by your email layouts.
              </p>
            </div>
          </div>

          {/* Search */}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:w-80">
              <Search
                size={17}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                placeholder="Search branding profiles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/10"
              />
            </div>

            <div className="text-xs text-slate-400">
              {filteredProfiles.length} profile
              {filteredProfiles.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {/* Error */}

        {isError && (
          <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Unable to load branding profiles.
          </div>
        )}

        {/* ===================================================== */}
        {/* Table */}
        {/* ===================================================== */}

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Profile
                </th>

                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Company
                </th>

                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Colors
                </th>

                <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Type
                </th>

                <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </th>

                <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredProfiles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-14 text-center">
                    <div className="flex flex-col items-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                        <ImageIcon size={21} className="text-slate-400" />
                      </div>

                      <p className="mt-3 text-sm font-medium text-slate-700">
                        No branding profiles found
                      </p>

                      <p className="mt-1 text-xs text-slate-400">
                        {search
                          ? "Try changing your search criteria."
                          : "Create your first branding profile."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredProfiles.map((profile) => (
                  <tr
                    key={profile.id}
                    className="border-b border-slate-100 transition hover:bg-slate-50/70"
                  >
                    {/* Profile */}

                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                          <BrandingLogo
                            src={profile.logoUrl}
                            name={profile.name}
                          />
                        </div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-medium text-slate-800">
                              {profile.name}
                            </p>

                            {profile.isDefault && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-600">
                                <Star size={10} fill="currentColor" />
                                Default
                              </span>
                            )}
                          </div>

                          <p className="mt-0.5 truncate text-xs text-slate-400">
                            {profile.tagLine || profile.websiteUrl}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Company */}

                    <td className="px-5 py-3.5">
                      <div>
                        <p className="text-sm text-slate-700">
                          {profile.companyName}
                        </p>

                        <p className="mt-0.5 text-xs text-slate-400">
                          {profile.supportEmail}
                        </p>
                      </div>
                    </td>

                    {/* Colors */}

                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <div
                          title={profile.primaryColor}
                          className="h-7 w-7 rounded-md border border-slate-200 shadow-sm"
                          style={{
                            backgroundColor: profile.primaryColor || "#000000",
                          }}
                        />

                        <div
                          title={profile.secondaryColor}
                          className="h-7 w-7 rounded-md border border-slate-200 shadow-sm"
                          style={{
                            backgroundColor:
                              profile.secondaryColor || "#FFFFFF",
                          }}
                        />

                        <div className="ml-1">
                          <p className="text-[10px] font-medium uppercase text-slate-400">
                            Primary
                          </p>

                          <p className="text-xs text-slate-600">
                            {profile.primaryColor || "-"}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Type */}

                    <td className="px-5 py-3.5 text-center">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                          profile.isDefault
                            ? "bg-amber-50 text-amber-600"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {profile.isDefault ? "Default" : "Custom"}
                      </span>
                    </td>

                    {/* Status */}

                    <td className="px-5 py-3.5 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                          profile.isActive
                            ? "bg-green-50 text-green-600"
                            : "bg-red-50 text-red-600"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            profile.isActive ? "bg-green-500" : "bg-red-500"
                          }`}
                        />

                        {profile.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* Actions */}

                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-center gap-2">
                        {/* Set Default */}

                        {!profile.isDefault && profile.isActive && (
                          <button
                            type="button"
                            title="Set as default"
                            disabled={isSettingDefault}
                            onClick={() => {
                              setDefaultId(profile.id);
                              setShowDefaultDialog(true);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-amber-600 transition hover:bg-amber-50 disabled:opacity-50"
                          >
                            <Star size={17} />
                          </button>
                        )}

                        {/* Edit */}

                        <button
                          type="button"
                          title="Edit"
                          onClick={() => handleEdit(profile.id)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-green-600 transition hover:bg-green-50"
                        >
                          <Pencil size={17} />
                        </button>

                        {/* Delete */}

                        <button
                          type="button"
                          title="Delete"
                          onClick={() => {
                            setDeleteId(profile.id);
                            setShowDeleteDialog(true);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-red-600 transition hover:bg-red-50"
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}

        <div className="border-t border-slate-200 bg-slate-50/50 px-5 py-3">
          <p className="text-xs text-slate-500">
            Showing{" "}
            <span className="font-semibold text-slate-700">
              {filteredProfiles.length}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-700">
              {profiles.length}
            </span>{" "}
            branding profiles
          </p>
        </div>
      </div>

      {/* ===================================================== */}
      {/* Delete Dialog */}
      {/* ===================================================== */}

      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-[420px] rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">
              Delete Branding Profile
            </h2>

            <p className="mt-3 text-sm leading-6 text-slate-500">
              Are you sure you want to delete this branding profile?
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteId(null);
                }}
                className="rounded-lg border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeleting && <Loader2 size={15} className="animate-spin" />}

                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================== */}
      {/* Set Default Dialog */}
      {/* ===================================================== */}

      {showDefaultDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-[420px] rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <Star size={19} fill="currentColor" />
              </div>

              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Set Default Branding Profile
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-500">
                  This profile will be used as the default branding profile for
                  email layouts.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDefaultDialog(false);
                  setDefaultId(null);
                }}
                className="rounded-lg border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSetDefault}
                disabled={isSettingDefault}
                className="inline-flex items-center gap-2 rounded-lg bg-[#2E7D32] px-5 py-2 text-sm font-medium text-white transition hover:bg-[#256B2A] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSettingDefault && (
                  <Loader2 size={15} className="animate-spin" />
                )}

                {isSettingDefault ? "Updating..." : "Set as Default"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BrandingProfilesScreen;
